import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled, { createGlobalStyle } from 'styled-components';
import { toast } from 'react-hot-toast';
import { adminService } from '../../services/admin.service';
import { FaEdit, FaTrash, FaPlus, FaUndo, FaStore, FaLink, FaEllipsisV } from 'react-icons/fa';
import AdminNavbar from '../../components/admin/AdminNavbar';
import Swal from 'sweetalert2';

const CampaignsList = () => {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openMenuId, setOpenMenuId] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    loadCampaigns();
  }, []);

  const loadCampaigns = async () => {
    try {
      const data = await adminService.getCampaigns();
      setCampaigns(data);
    } catch (error) {
      toast.error('Error al cargar las campañas');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('¿Estás seguro de eliminar esta campaña?')) {
      try {
        await adminService.deleteCampaign(id);
        toast.success('Campaña eliminada correctamente');
        loadCampaigns();
      } catch (error) {
        toast.error('Error al eliminar la campaña');
      }
    }
  };

  const handleReset = async (campaign) => {
    const result = await Swal.fire({
      title: '¿Resetear campaña?',
      html: `
        <p>Esta acción:</p>
        <ul style="text-align: left; margin-top: 1rem;">
          <li>Eliminará todas las participaciones</li>
          <li>Restaurará el número de premios disponibles</li>
        </ul>
        <p style="margin-top: 1rem;">Esta acción no se puede deshacer.</p>
      `,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#ef4444',
      cancelButtonColor: '#6B7280',
      confirmButtonText: 'Sí, resetear',
      cancelButtonText: 'Cancelar'
    });

    if (result.isConfirmed) {
      try {
        await adminService.resetCampaign(campaign._id);
        toast.success('Campaña reseteada correctamente');
        loadCampaigns();
      } catch (error) {
        toast.error('Error al resetear la campaña');
      }
    }
  };

  const getEffectiveHostnames = (campaign) => {
    if (Array.isArray(campaign?.domains) && campaign.domains.length > 0) {
      return [...campaign.domains].map(h => String(h).trim()).filter(Boolean);
    }
    const d = campaign?.domain && String(campaign.domain).trim();
    return d ? [d] : [];
  };

  const formatCampaignDomainsLabel = (campaign) => {
    const hosts = getEffectiveHostnames(campaign);
    if (hosts.length === 0) return 'Todos';
    if (hosts.length <= 2) return hosts.join(', ');
    return `${hosts.length} dominios`;
  };

  const getBaseUrl = (campaign) => {
    const hosts = getEffectiveHostnames(campaign);
    const domain = hosts[0];
    return domain
      ? (domain.startsWith('http') ? domain : `${domain.includes('localhost') ? 'http' : 'https'}://${domain}`)
      : window.location.origin;
  };

  const getBusinessDashboardUrl = (campaign, adminUrl) => {
    return `${getBaseUrl(campaign).replace(/\/$/, '')}/business/${adminUrl}`;
  };

  const getCampaignUrl = (campaign) => {
    return `${getBaseUrl(campaign).replace(/\/$/, '')}/campaign/${campaign.slug}`;
  };

  const copyCampaignUrl = async (campaign) => {
    const url = getCampaignUrl(campaign);
    try {
      await navigator.clipboard.writeText(url);
      toast.success('URL copiada al portapapeles');
    } catch (error) {
      toast.error('Error al copiar la URL');
    }
  };

  const showBusinessCredentials = async (campaign) => {
    const dashboardUrl = getBusinessDashboardUrl(campaign, campaign.business.adminUrl);
    const credentialsText = `Usuario: ${campaign.business.username}\nURL: ${dashboardUrl}`;

    const result = await Swal.fire({
      title: 'Acceso Dashboard Business',
      html: `
        <div style="text-align: left; padding: 1rem;">
          <p style="margin: 0.5rem 0;"><strong>Usuario:</strong> ${campaign.business.username}</p>
          <p style="margin: 0.5rem 0;"><strong>URL:</strong> <a href="${dashboardUrl}" target="_blank" rel="noopener" style="color: #60a5fa; word-break: break-all;">${dashboardUrl}</a></p>
          <button type="button" id="copy-credentials-btn" style="margin-top: 1rem; padding: 0.5rem 1rem; background: rgba(59, 130, 246, 0.2); color: #60a5fa; border: 1px solid rgba(59, 130, 246, 0.4); border-radius: 8px; cursor: pointer; display: flex; align-items: center; gap: 0.5rem;">
            📋 Copiar credenciales
          </button>
        </div>
      `,
      icon: 'info',
      showCancelButton: true,
      showDenyButton: true,
      confirmButtonText: 'Cerrar',
      denyButtonText: 'Resetear contraseña',
      denyButtonColor: '#EF4444',
      customClass: {
        popup: 'business-credentials-popup',
        title: 'business-credentials-title',
        htmlContainer: 'business-credentials-content',
        confirmButton: 'business-credentials-button',
        denyButton: 'business-credentials-reset-button'
      },
      didOpen: () => {
        const copyBtn = document.getElementById('copy-credentials-btn');
        if (copyBtn) {
          copyBtn.onclick = () => {
            navigator.clipboard.writeText(credentialsText).then(() => {
              toast.success('Credenciales copiadas al portapapeles');
              copyBtn.textContent = '✓ Copiado';
              copyBtn.style.background = 'rgba(34, 197, 94, 0.2)';
              copyBtn.style.color = '#4ade80';
            });
          };
        }
      }
    });

    if (result.isDenied) {
      // Confirmar reset de contraseña
      const confirmReset = await Swal.fire({
        title: '¿Resetear contraseña?',
        text: 'Se generará una nueva contraseña. Esta acción no se puede deshacer.',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#EF4444',
        cancelButtonColor: '#6B7280',
        confirmButtonText: 'Sí, resetear',
        cancelButtonText: 'Cancelar'
      });

      if (confirmReset.isConfirmed) {
        try {
          const response = await adminService.resetBusinessPassword(campaign._id);
          const newDashboardUrl = getBusinessDashboardUrl(campaign, response.credentials.adminUrl);
          const newCredentialsText = `Usuario: ${response.credentials.username}\nContraseña: ${response.credentials.password}\nURL: ${newDashboardUrl}`;

          await Swal.fire({
            title: 'Nueva contraseña generada',
            html: `
              <div style="text-align: left; padding: 1rem;">
                <p style="margin: 0.5rem 0;"><strong>Usuario:</strong> ${response.credentials.username}</p>
                <p style="margin: 0.5rem 0;"><strong>Contraseña:</strong> ${response.credentials.password}</p>
                <p style="margin: 0.5rem 0;"><strong>URL:</strong> <a href="${newDashboardUrl}" target="_blank" rel="noopener" style="color: #60a5fa; word-break: break-all;">${newDashboardUrl}</a></p>
                <button type="button" id="copy-new-credentials-btn" style="margin-top: 1rem; padding: 0.5rem 1rem; background: rgba(59, 130, 246, 0.2); color: #60a5fa; border: 1px solid rgba(59, 130, 246, 0.4); border-radius: 8px; cursor: pointer; display: flex; align-items: center; gap: 0.5rem;">
                  📋 Copiar credenciales
                </button>
              </div>
            `,
            icon: 'success',
            customClass: {
              popup: 'business-credentials-popup',
              title: 'business-credentials-title',
              htmlContainer: 'business-credentials-content',
              confirmButton: 'business-credentials-button'
            },
            didOpen: () => {
              const copyBtn = document.getElementById('copy-new-credentials-btn');
              if (copyBtn) {
                copyBtn.onclick = () => {
                  navigator.clipboard.writeText(newCredentialsText).then(() => {
                    toast.success('Credenciales copiadas al portapapeles');
                    copyBtn.textContent = '✓ Copiado';
                    copyBtn.style.background = 'rgba(34, 197, 94, 0.2)';
                    copyBtn.style.color = '#4ade80';
                  });
                };
              }
            }
          });
        } catch (error) {
          toast.error('Error al resetear la contraseña');
        }
      }
    }
  };

  if (loading) {
    return <LoadingMessage>Cargando campañas...</LoadingMessage>;
  }

  return (
    <>
      <GlobalStyle />
      <AdminNavbar />
      <Container>
        <Header>
          <Title>Campañas</Title>
          <AddButton onClick={() => navigate('/pmpl/admin/campaigns/new')}>
            <FaPlus /> Nueva Campaña
          </AddButton>
        </Header>

        <TableWrapper>
          <Table>
            <thead>
              <tr>
                <Th>Nombre</Th>
                <Th>Dominio</Th>
                <Th>Fechas</Th>
                <Th>Premios</Th>
                <ThActions>Acciones</ThActions>
              </tr>
            </thead>
            <tbody>
              {campaigns.map(campaign => (
                <tr key={campaign._id}>
                  <Td>
                    <CampaignNameCell>
                      <span>{campaign.name}</span>
                      <StatusBadge active={campaign.isActive}>
                        {campaign.isActive ? 'Activa' : 'Inactiva'}
                      </StatusBadge>
                    </CampaignNameCell>
                  </Td>
                  <Td>{formatCampaignDomainsLabel(campaign)}</Td>
                  <Td>
                    <DatesCell>
                      {new Date(campaign.startDate).toLocaleDateString('es-ES', { day: '2-digit', month: 'short' })} – {new Date(campaign.endDate).toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' })}
                    </DatesCell>
                  </Td>
                  <Td>{campaign.prizes.length}</Td>
                  <TdActions>
                    <ActionsCell>
                      <IconBtn onClick={() => copyCampaignUrl(campaign)} title="Copiar URL">
                        <FaLink />
                      </IconBtn>
                      <IconBtn onClick={() => navigate(`/pmpl/admin/campaigns/${campaign._id}/edit`)} title="Editar">
                        <FaEdit />
                      </IconBtn>
                      <IconBtn onClick={() => showBusinessCredentials(campaign)} title="Dashboard">
                        <FaStore />
                      </IconBtn>
                      <MenuWrapper>
                        <MenuTrigger
                          onClick={() => setOpenMenuId(openMenuId === campaign._id ? null : campaign._id)}
                          $active={openMenuId === campaign._id}
                        >
                          <FaEllipsisV />
                        </MenuTrigger>
                        {openMenuId === campaign._id && (
                          <>
                            <Backdrop onClick={() => setOpenMenuId(null)} />
                            <MenuDropdown>
                              <MenuItem onClick={() => { handleReset(campaign); setOpenMenuId(null); }}>
                                <FaUndo /> Resetear
                              </MenuItem>
                              <MenuItem danger onClick={() => { handleDelete(campaign._id); setOpenMenuId(null); }}>
                                <FaTrash /> Eliminar
                              </MenuItem>
                            </MenuDropdown>
                          </>
                        )}
                      </MenuWrapper>
                    </ActionsCell>
                  </TdActions>
                </tr>
              ))}
            </tbody>
          </Table>
        </TableWrapper>
      </Container>
    </>
  );
};

const Container = styled.div`
  padding: 2rem;
  max-width: 1200px;
  margin: 0 auto;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
`;

const Title = styled.h1`
  color: #ffd700;
  margin: 0;
`;

const AddButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  background: #ffd700;
  color: #1a1a1a;
  border: none;
  border-radius: 4px;
  font-weight: bold;
  cursor: pointer;

  &:hover {
    background: #ffed4a;
  }
`;

const TableWrapper = styled.div`
  background: #2a2a2a;
  border-radius: 8px;
  overflow-x: auto;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);

  @media (max-width: 768px) {
    margin: 0 -1rem;
    border-radius: 0;
  }
`;

const Table = styled.table`
  width: 100%;
  min-width: 600px;
  border-collapse: collapse;
`;

const Th = styled.th`
  text-align: left;
  padding: 1rem 1.25rem;
  font-size: 0.8rem;
  font-weight: 600;
  color: #999;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  border-bottom: 1px solid #333;

  &:first-child {
    padding-left: 1.5rem;
  }
`;

const ThActions = styled(Th)`
  text-align: right;
  width: 140px;
  padding-right: 1rem;
`;

const Td = styled.td`
  padding: 1rem 1.25rem;
  color: #fff;
  border-bottom: 1px solid #333;
  vertical-align: middle;

  &:first-child {
    padding-left: 1.5rem;
  }
`;

const TdActions = styled(Td)`
  text-align: right;
  padding-right: 1rem;
`;

const CampaignNameCell = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const StatusBadge = styled.span`
  padding: 0.2rem 0.5rem;
  border-radius: 6px;
  font-size: 0.7rem;
  font-weight: 600;
  background: ${props => props.active ? 'rgba(52, 211, 153, 0.2)' : 'rgba(239, 68, 68, 0.2)'};
  color: ${props => props.active ? '#34D399' : '#EF4444'};
`;

const DatesCell = styled.span`
  font-size: 0.9rem;
  color: #aaa;
`;

const ActionsCell = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.25rem;
`;

const IconBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
  border: none;
  border-radius: 6px;
  background: rgba(255, 255, 255, 0.05);
  color: #999;
  cursor: pointer;
  transition: all 0.15s;

  &:hover {
    background: rgba(255, 215, 0, 0.15);
    color: #ffd700;
  }
`;

const MenuWrapper = styled.div`
  position: relative;
`;

const MenuTrigger = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
  border: none;
  border-radius: 6px;
  background: ${props => props.$active ? 'rgba(255, 255, 255, 0.1)' : 'rgba(255, 255, 255, 0.05)'};
  color: #999;
  cursor: pointer;
  transition: all 0.15s;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
    color: #fff;
  }
`;

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  z-index: 9;
`;

const MenuDropdown = styled.div`
  position: absolute;
  right: 0;
  top: 100%;
  margin-top: 4px;
  min-width: 160px;
  background: #1a1a1a;
  border: 1px solid #333;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  z-index: 10;
  overflow: hidden;
`;

const MenuItem = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  width: 100%;
  padding: 0.6rem 1rem;
  border: none;
  background: transparent;
  color: ${props => props.danger ? '#EF4444' : '#fff'};
  font-size: 0.9rem;
  cursor: pointer;
  text-align: left;
  transition: background 0.15s;

  &:hover {
    background: rgba(255, 255, 255, 0.08);
  }
`;

const LoadingMessage = styled.div`
  text-align: center;
  color: #fff;
  padding: 2rem;
`;

// Estilos globales para el modal de Sweetalert2
const GlobalStyle = createGlobalStyle`
  .business-credentials-popup {
    background: linear-gradient(135deg, 
      rgba(88, 28, 135, 0.95) 0%,
      rgba(67, 20, 102, 0.95) 100%
    ) !important;
    border: 1px solid rgba(59, 130, 246, 0.2) !important;
    border-radius: 20px !important;
    padding: 2rem !important;
  }

  .business-credentials-title {
    color: #3b82f6 !important;
    font-size: 1.5rem !important;
  }

  .business-credentials-content {
    color: #fff !important;
    font-size: 1.1rem !important;
    
    p {
      background: rgba(59, 130, 246, 0.1);
      padding: 0.75rem;
      border-radius: 8px;
      border: 1px solid rgba(59, 130, 246, 0.2);
    }
  }

  .business-credentials-button {
    background: rgba(59, 130, 246, 0.1) !important;
    color: #3b82f6 !important;
    border: 1px solid rgba(59, 130, 246, 0.2) !important;
    padding: 0.75rem 2rem !important;
    font-size: 1.1rem !important;
    border-radius: 8px !important;

    &:hover {
      background: rgba(59, 130, 246, 0.2) !important;
    }
  }
`;

export default CampaignsList;